import { format, formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import {
  Brain,
  Fish,
  Trash2,
  ChevronRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react'
import type { AIAnalysisResponse, Severity } from '../types'

// Disease code to full name mapping
const DISEASE_NAMES: Record<string, string> = {
  ICH: 'Ichthyophthirius multifiliis',
  FLU: 'Gyrodactylus species (Skin Flukes)',
  DAC: 'Dactylogyrus species (Gill Flukes)',
  COS: 'Costia necatrix',
  TRI: 'Trichodina species',
  CHI: 'Chilodonella cyprini',
  ARG: 'Argulus species (Fish Lice)',
  LER: 'Lernaea species (Anchor Worm)',
  ANI: 'Anisakis species',
  DIP: 'Diplostomum species (Eye Flukes)',
  POST: 'Posthodiplostomum species (Black Spot)',
  PRO: 'Proteocephalus species (Tapeworm)',
  BOT: 'Bothriocephalus species (Tapeworm)',
  LIG: 'Ligula intestinalis (Tapeworm)',
  PHI: 'Philometra species',
  CAM: 'Camallanus species (Red Worm)',
  CAP: 'Capillaria species',
  CON: 'Contracaecum species',
  EUS: 'Eustrongylides species',
  SPH: 'Sphincterodiplostomum species',
  AER: 'Aeromonas hydrophila',
  VIB: 'Vibrio species (Vibriosis)',
  COL: 'Flavobacterium columnare (Columnaris)',
  STR: 'Streptococcus species',
  FUN: 'Saprolegnia species (Fungal)',
  FIN: 'Fin Rot',
  ERM: 'Yersinia ruckeri (Enteric Redmouth)',
  AGD: 'Amoebic Gill Disease',
  CAR: 'Cardicola species (Blood Fluke)',
  VNN: 'Viral Nervous Necrosis',
  WSSV: 'White Spot Syndrome Virus',
  YHV: 'Yellowhead Virus',
  QXD: 'QX Disease',
  POMS: 'Pacific Oyster Mortality Syndrome',
  SDD: 'Scale Drop Disease',
  SEA: 'Sea Lice',
  EHP: 'Enterocytozoon hepatopenaei',
  AVG: 'Abalone Viral Ganglioneuritis',
  EHNV: 'Epizootic Haematopoietic Necrosis Virus',
  BON: 'Bonamiosis',
}

// Format the identified condition for subtext display
function formatIdentifiedCondition(condition: string | undefined): string | null {
  if (!condition) return null

  // Check if the condition is a known code
  const conditionUpper = condition.toUpperCase()
  const fullName = DISEASE_NAMES[conditionUpper]

  if (fullName) {
    return `${fullName} (${conditionUpper})`
  }

  // Return as-is if not a known code (might already be a full name)
  return condition
}

interface AIHistoryTableProps {
  analyses: AIAnalysisResponse[]
  pondNameMap: Record<string, string>
  farmId: string
  onDelete: (id: string) => void
  isDeleting?: string
}

const SEVERITY_STYLES: Record<
  Exclude<Severity, 'none'>,
  {
    border: string
    bg: string
    badge: string
    icon: React.ElementType
    pulse?: boolean
  }
> = {
  critical: {
    border: 'border-l-4 border-l-red-500',
    bg: 'bg-gradient-to-r from-red-50/60 to-transparent',
    badge: 'bg-red-50 text-red-700 border border-red-200/50',
    icon: XCircle,
    pulse: true,
  },
  high: {
    border: 'border-l-4 border-l-orange-500',
    bg: 'bg-gradient-to-r from-orange-50/40 to-transparent',
    badge: 'bg-orange-50 text-orange-700 border border-orange-200/50',
    icon: AlertTriangle,
  },
  medium: {
    border: 'border-l-4 border-l-yellow-500',
    bg: '',
    badge: 'bg-yellow-50 text-yellow-700 border border-yellow-200/50',
    icon: AlertCircle,
  },
  low: {
    border: 'border-l-4 border-l-green-500',
    bg: '',
    badge: 'bg-green-50 text-green-700 border border-green-200/50',
    icon: CheckCircle,
  },
}

export function AIHistoryTable({
  analyses,
  pondNameMap,
  farmId,
  onDelete,
  isDeleting,
}: AIHistoryTableProps) {
  return (
    <div className="rounded-xl border border-slate-200/50 bg-white overflow-hidden shadow-sm">
      <table className="w-full">
        <thead>
          <tr className="border-b border-slate-100">
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/50 w-[100px]">
              Severity
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/50">
              Condition / Analysis
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/50 w-[140px]">
              Pond
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/50 w-[80px]">
              Conf.
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/50 w-[100px]">
              Date
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider bg-slate-50/50 w-[100px]">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {analyses.map((analysis) => {
            const severity = (analysis.severity || 'medium') as Exclude<Severity, 'none'>
            const styles = SEVERITY_STYLES[severity] || SEVERITY_STYLES.medium
            const SeverityIcon = styles.icon
            const isRootCause = analysis.analysisType === 'root_cause'
            const TypeIcon = isRootCause ? Brain : Fish
            const pondName = pondNameMap[analysis.pondId] || 'Unknown Pond'
            const title = isRootCause ? 'Root Cause Analysis' : 'Fish Health Diagnosis'
            const identifiedCondition = formatIdentifiedCondition(analysis.identifiedCondition)

            return (
              <tr
                key={analysis.id}
                className={`border-b border-slate-100 last:border-b-0 transition-colors hover:bg-slate-50/50 ${styles.border} ${styles.bg}`}
              >
                {/* Severity */}
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${styles.badge} ${
                      styles.pulse ? 'animate-pulse' : ''
                    }`}
                  >
                    <SeverityIcon className="h-3.5 w-3.5" />
                    {severity.charAt(0).toUpperCase() + severity.slice(1)}
                  </span>
                </td>

                {/* Condition */}
                <td className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-50 mt-0.5">
                      <TypeIcon className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 truncate">{title}</p>
                      {identifiedCondition && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">
                          Identified: {identifiedCondition}
                        </p>
                      )}
                    </div>
                  </div>
                </td>

                {/* Pond */}
                <td className="px-4 py-3">
                  <span className="text-sm text-slate-700">{pondName}</span>
                </td>

                {/* Confidence */}
                <td className="px-4 py-3">
                  {analysis.confidenceScore ? (
                    <span className="text-sm font-medium text-slate-700">
                      {Math.round(analysis.confidenceScore * 100)}%
                    </span>
                  ) : (
                    <span className="text-sm text-slate-400">-</span>
                  )}
                </td>

                {/* Date */}
                <td className="px-4 py-3">
                  <span
                    className="text-sm text-slate-500"
                    title={format(new Date(analysis.createdAt), 'PPpp')}
                  >
                    {formatDistanceToNow(new Date(analysis.createdAt), {
                      addSuffix: true,
                    })}
                  </span>
                </td>

                {/* Actions */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => {
                        if (confirm('Are you sure you want to delete this analysis?')) {
                          onDelete(analysis.id)
                        }
                      }}
                      disabled={isDeleting === analysis.id}
                      className="p-1.5 rounded text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-50"
                      title="Delete analysis"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                    <Link
                      to={`/farms/${farmId}/ai/${analysis.id}`}
                      className="flex items-center gap-1 px-2 py-1 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
                    >
                      View
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default AIHistoryTable
